import React from 'react';

/**
 * Custom (product-specific) glyphs for the hybrid icon registry.
 *
 * These are bespoke marks that have no clean lucide-react equivalent. They are
 * authored to the SAME call signature lucide passes its icons — `size`,
 * `strokeWidth`, `className`, and any spread a11y props — so <Icon> can treat a
 * custom glyph and a lucide glyph identically. Keep them 24×24, stroke-based,
 * `stroke="currentColor"` so `tone` (which sets `color`) and size both work.
 *
 * Add a new custom glyph here, then register its semantic name in `registry.js`.
 */

/** Threat — shield with an exclamation (matches the legacy NotificationIcons). */
export const ThreatGlyph = ({ size = 24, strokeWidth = 2, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/** CAL / risk matrix — crosshair grid (matches the legacy ConfigIcons). */
export const CalMatrixGlyph = ({ size = 24, strokeWidth = 2, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v18M3 12h18" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

/* ── Architecture-diagram electronics marks (no clean lucide match) ── */

/** Transistor — base lead + bar + collector/emitter (discrete semiconductors). */
export const TransistorGlyph = ({ size = 24, strokeWidth = 2, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <line x1="3" y1="12" x2="9" y2="12" />
    <line x1="9" y1="6" x2="9" y2="18" />
    <line x1="9" y1="9.5" x2="18" y2="5" />
    <line x1="9" y1="14.5" x2="18" y2="19" />
  </svg>
);

/** Resistor — zigzag (passives: resistor / capacitor / inductor). */
export const ResistorGlyph = ({ size = 24, strokeWidth = 2, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M2 12h3l2-5 3 10 3-10 3 10 2-5h3" />
  </svg>
);

/** Op-amp — triangle with two inputs + output (analog ICs). */
export const OpAmpGlyph = ({ size = 24, strokeWidth = 2, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M6 4v16l13-8z" />
    <line x1="2" y1="9" x2="6" y2="9" />
    <line x1="2" y1="15" x2="6" y2="15" />
    <line x1="19" y1="12" x2="22" y2="12" />
  </svg>
);

/** GitHub mark — octocat silhouette (brand mark; lucide dropped brand
 *  icons, so it lives here). Fill-based by nature — `currentColor` fill
 *  keeps tone/size working like the stroke glyphs. */
export const GitHubGlyph = ({ size = 24, strokeWidth: _strokeWidth, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none" {...props}>
    <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5c.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34c-.46-1.16-1.11-1.47-1.11-1.47c-.91-.62.07-.6.07-.6c1 .07 1.53 1.03 1.53 1.03c.87 1.52 2.34 1.07 2.91.83c.09-.65.35-1.09.63-1.34c-2.22-.25-4.55-1.11-4.55-4.92c0-1.11.38-2 1.03-2.71c-.1-.25-.45-1.29.1-2.64c0 0 .84-.27 2.75 1.02c.79-.22 1.65-.33 2.5-.33c.85 0 1.71.11 2.5.33c1.91-1.29 2.75-1.02 2.75-1.02c.55 1.35.2 2.39.1 2.64c.65.71 1.03 1.6 1.03 2.71c0 3.82-2.34 4.66-4.57 4.91c.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2Z" />
  </svg>
);
